/**
 * PanelSelectionService — 지도 피처 선택/해제 + 트리-지도 매핑
 *
 * Pure JS: OL Collection 의존성만. Vue 의존성 없음.
 */
import Collection from 'ol/Collection';
import { diffOlFeatureCollection } from '@/features/panel-planning/utils/panelMapSelection';
import {
  getPanelMapFeaturesForString,
  getPanelMapFeatureForModuleSeq,
  getPanelMapStringAnchorForString,
  getPanelMapStringAnchorsForArray
} from '@/features/panel-planning/utils/panelMapFeatureIndex';
import { resolveSolarArraySeqFromTreeItem, resolveSolarStringSeqFromTreeItem } from '@/features/panel-planning/utils/flattenModuleTreeRows';
import { readModuleSeq, readStringSeq } from '@/features/panel-planning/utils/panelMapFeatureProps';

export function createPanelSelectionService() {
  let groupSelection = new Collection();
  let moduleSelection = new Collection();

  return {
    getGroupSelection: () => groupSelection,
    getModuleSelection: () => moduleSelection,

    init() {
      groupSelection = new Collection();
      moduleSelection = new Collection();
    },

    setGroupSelection(targetFeatures, callbacks = {}) {
      moduleSelection.clear();
      diffOlFeatureCollection(groupSelection, targetFeatures || []);
      callbacks.onRefresh?.();
      callbacks.onTranslateUpdate?.();
      callbacks.onStyleRefresh?.();
    },

    setModuleSelection(targetFeatures, callbacks = {}) {
      groupSelection.clear();
      diffOlFeatureCollection(moduleSelection, targetFeatures || []);
      callbacks.onRefresh?.();
      callbacks.onTranslateUpdate?.();
      callbacks.onStyleRefresh?.();
    },

    clearGroup(callbacks = {}) {
      groupSelection.clear();
      callbacks.onHighlightClear?.();
      callbacks.onRefresh?.();
      callbacks.onTranslateUpdate?.();
      callbacks.onStyleRefresh?.();
    },

    clearModule(callbacks = {}) {
      moduleSelection.clear();
      callbacks.onHighlightClear?.();
      callbacks.onRefresh?.();
      callbacks.onTranslateUpdate?.();
      callbacks.onStyleRefresh?.();
    },

    clearAll(callbacks = {}) {
      this.clearGroup(callbacks);
      this.clearModule(callbacks);
    },

    expandSparseSelectionForHighlight(anchorFeatures, featureIndex) {
      if (!anchorFeatures?.length) return [];
      if (!featureIndex) return anchorFeatures;
      const seen = new Set();
      const expanded = [];
      anchorFeatures.forEach(anchor => {
        const stringSeq = readStringSeq(anchor);
        const related = stringSeq != null
          ? getPanelMapFeaturesForString(featureIndex, String(stringSeq))
          : [anchor];
        related.forEach(feature => {
          if (!seen.has(feature)) {
            seen.add(feature);
            expanded.push(feature);
          }
        });
      });
      return expanded;
    },

    /**
     * 트리 노드 → 지도 피처 매핑
     * @param {object} item - { type, solarModuleId, solarStringId, solarArrayId, children, ... }
     * @param {object} featureIndex - from getPanelMapFeatureIndex()
     * @param {object} vectorSource - OL VectorSource (fallback)
     */
    resolveMapFeaturesForTreeItem(item, featureIndex, vectorSource) {
      const empty = { groupFeatures: [], moduleFeatures: [] };
      if (!item?.type) return empty;

      if (item.type === 'module') {
        const moduleSeq = item.solarModuleSeq ?? item.seq;
        if (moduleSeq != null && moduleSeq !== '' && featureIndex) {
          const feature = getPanelMapFeatureForModuleSeq(featureIndex, String(moduleSeq));
          return { groupFeatures: [], moduleFeatures: feature ? [feature] : [] };
        }
        const modDisplayId = item.solarModuleId || item.moduleId;
        if (vectorSource && modDisplayId != null) {
          const stringSeq = item.solarStringSeq ?? item.stringSeq ?? null;
          const moduleFeatures = vectorSource.getFeatures().filter(f => {
            const fDisplay = f.get('solarModuleId') ?? f.get('moduleDisplayNo');
            if (String(fDisplay) !== String(modDisplayId)) return false;
            if (stringSeq == null) return true;
            return readStringSeq(f) === String(stringSeq);
          });
          return { groupFeatures: [], moduleFeatures };
        }
        return { groupFeatures: [], moduleFeatures: [] };
      }

      if (item.type === 'string') {
        const stringSeq = resolveSolarStringSeqFromTreeItem(item);
        const anchor = stringSeq != null && featureIndex
          ? getPanelMapStringAnchorForString(featureIndex, String(stringSeq))
          : null;
        return { groupFeatures: anchor ? [anchor] : [], moduleFeatures: [] };
      }

      if (item.type === 'array') {
        return this._resolveArrayFeatures(item, featureIndex);
      }

      return empty;
    },

    _resolveArrayFeatures(item, featureIndex) {
      const gfs = [];
      const seen = new Set();
      const pushAnchor = anchor => {
        if (anchor && !seen.has(anchor)) {
          seen.add(anchor);
          gfs.push(anchor);
        }
      };

      // byArrayId 키는 DB seq만 — 표시용 arrayId('2')는 다른 Array(seq=2)와 충돌
      const arraySeq = resolveSolarArraySeqFromTreeItem(item);
      if (featureIndex && arraySeq != null) {
        getPanelMapStringAnchorsForArray(featureIndex, String(arraySeq)).forEach(pushAnchor);
      }

      // 트리 children — byArrayId stale(이동 직후)이어도 stringSeq로 앵커 복구
      if (item.children?.length && featureIndex) {
        item.children
          .filter(child => child.type === 'string')
          .forEach(stringNode => {
            const stringSeq = resolveSolarStringSeqFromTreeItem(stringNode);
            if (stringSeq != null) {
              pushAnchor(getPanelMapStringAnchorForString(featureIndex, String(stringSeq)));
            }
          });
      }

      return { groupFeatures: gfs, moduleFeatures: [] };
    }
  };
}
